"use client";

import { useState } from "react";
import { LuCheck, LuPlus } from "react-icons/lu";
import TextComponent from "@/app/_components/TextComponent";
import cn from "@/utils/cn";
import { useZStore } from "@/utils/z-store";
import { getEffectivePlanType } from "@/utils/subscription-utils";
import { getMaxOwnedWorkspaces } from "@/shared/workspace-permissions";
import { useWorkspaces } from "../_hooks/useWorkspaces";

/**
 * The "Switch Workspace" block inside the UserProfileSidebar dropdown: lists the
 * workspaces the user belongs to (checkmark on the active one) and offers a
 * premium-gated "Add Workspace" action with an inline create form.
 */
export default function WorkspaceSwitcher() {
  const {
    workspaces,
    activeWorkspaceId,
    switchWorkspace,
    workspacesLoaded,
    createWorkspace,
  } = useWorkspaces();
  const { data, actions } = useZStore();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Nothing to show until the first load resolves (avoids an empty flash).
  if (!workspacesLoaded && workspaces.length === 0) return null;

  // Mirror the server gate (getMaxOwnedWorkspaces) client-side.
  const ownedCount = workspaces.filter((w) => w.isOwner).length;
  const effectivePlan = getEffectivePlanType(
    data.subscriptionPlan,
    data.subscriptionStatus,
  );
  const canCreate = ownedCount < getMaxOwnedWorkspaces(effectivePlan);

  const handleAdd = () => {
    if (!canCreate) {
      actions.openModal({ modalName: "billing-modal" });
      return;
    }
    setCreating(true);
  };

  const submitCreate = async () => {
    if (!newName.trim() || submitting) return;
    setSubmitting(true);
    const ok = await createWorkspace(newName);
    // On success the hook navigates away; only reset on failure.
    if (!ok) {
      setSubmitting(false);
    } else {
      setNewName("");
      setCreating(false);
    }
  };

  return (
    <section className="border-b py-2">
      <TextComponent
        type="caption"
        className="px-[19px] py-1 text-[10px] font-medium uppercase tracking-wide text-kd-secondary-3 xl:px-[26px]"
      >
        Switch Workspace
      </TextComponent>

      <ul>
        {workspaces.map((w) => {
          const isActive = w.id === activeWorkspaceId;
          return (
            <li key={w.id}>
              <button
                type="button"
                onClick={() => !isActive && switchWorkspace(w.id)}
                className="flex w-full items-center gap-3 px-[19px] py-2 text-left transition-colors hover:bg-kd-secondary-6 xl:px-[26px]"
              >
                <span className="flex w-4 shrink-0 justify-center">
                  {isActive && <LuCheck className="size-4 text-kd-lte-blue" />}
                </span>
                <TextComponent
                  type="body"
                  className={cn(
                    "min-w-0 flex-1 truncate text-kd-secondary-1",
                    isActive && "font-semibold",
                  )}
                >
                  {w.name}
                </TextComponent>
                {/* Role badge (§WS3): the caller's access level in this
                    workspace, at a glance. Owner outranks the admin role. */}
                <span className="shrink-0 rounded bg-kd-secondary-6 px-1.5 py-0.5 text-[9px] font-medium capitalize text-kd-secondary-2">
                  {w.isOwner ? "Owner" : w.role}
                </span>
                {w.status === "frozen" && (
                  <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-600">
                    Frozen
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Add Workspace (highlighted) / inline create form */}
      {creating ? (
        <div className="flex items-center gap-1.5 px-[19px] py-2 xl:px-[26px]">
          <input
            autoFocus
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitCreate();
              if (e.key === "Escape") {
                setCreating(false);
                setNewName("");
              }
            }}
            placeholder="Workspace name"
            disabled={submitting}
            className="min-w-0 flex-1 rounded-[6px] border border-kd-secondary-6 px-2 py-1 text-[12px] text-kd-secondary-1 outline-none focus:border-kd-lte-blue"
          />
          <button
            type="button"
            onClick={submitCreate}
            disabled={!newName.trim() || submitting}
            className={cn(
              "rounded-[6px] bg-kd-lte-blue px-2 py-1 text-[12px] font-medium text-white",
              (!newName.trim() || submitting) && "opacity-50",
            )}
          >
            {submitting ? "…" : "Add"}
          </button>
          <button
            type="button"
            onClick={() => {
              setCreating(false);
              setNewName("");
            }}
            disabled={submitting}
            className="text-[12px] text-kd-secondary-3 hover:text-kd-secondary-1"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleAdd}
          className="mt-1 flex w-full items-center gap-3 bg-kd-lte-blue/10 px-[19px] py-2.5 text-left transition-colors hover:bg-kd-lte-blue/20 xl:px-[26px]"
        >
          <span className="flex w-4 shrink-0 justify-center">
            <LuPlus className="size-4 text-kd-lte-blue" />
          </span>
          <TextComponent type="body" className="font-medium text-kd-lte-blue">
            Add Workspace
          </TextComponent>
        </button>
      )}
    </section>
  );
}
